import React, { useState } from 'react';
import SelectField from './SelectField';
import TextInput from './TextInput';
import { FormData } from '../types';
import '../styles/Section.css';

interface Props {
  formData: FormData;
  onChange: (field: keyof FormData, value: string) => void;
}

const VideoSection: React.FC<Props> = ({ formData, onChange }) => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isVideo1Expanded, setIsVideo1Expanded] = useState<boolean>(false);
  const [isVideo2Expanded, setIsVideo2Expanded] = useState<boolean>(false);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
  };

  const generateVideoWithKlingAI = async () => {
    if (!uploadedImage) {
      alert('Please upload an image first');
      return;
    }

    setIsGenerating(true);
    
    try {
      // Step 1: Create video generation task
      const accessKey = 'ACfGYapBpPAPhmTL9CbDpBRRfKPeaDdY';
      const secretKey = 'r9DKLprnCfYGCa4b4femkGN83tTAyFtr';

      // Build prompt from form data
      let prompt = `Professional jewelry video showcasing ${formData.jewelryItem || 'jewelry'}. `;
      prompt += `Camera angle: ${formData.cameraAngle || 'Eye Level'}. `;
      prompt += `Camera movement: ${formData.cameraMovement || 'Slow Pan'}. `;
      prompt += `Motion speed: ${formData.motionSpeed || 'Slow'}. `;
      prompt += `Focus point: ${formData.focusPoint || 'On Jewelry'}.`;
      
      if (formData.videoOtherDetails.trim() !== '') {
        prompt += ` ${formData.videoOtherDetails.trim()}.`;
      }

      prompt += ` High-resolution cinematic video, professional lighting, smooth motion, luxury aesthetic.`;

      const createResponse = await fetch('https://api.klingai.com/v1/videos/image2video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessKey}:${secretKey}`
        },
        body: JSON.stringify({
          model_name: 'kling-v1',
          image: uploadedImage,
          prompt: prompt,
          duration: '5',
          aspect_ratio: formData.aspectRatio || '16:9',
          cfg_scale: 0.5,
          camera_control: {
            type: formData.cameraMovement || 'Slow Pan'
          }
        })
      });

      const createData = await createResponse.json();
      
      if (createData.code === 0 && createData.data?.task_id) {
        const newTaskId = createData.data.task_id;
        setTaskId(newTaskId);
        
        // Step 2: Poll for video completion
        pollVideoStatus(newTaskId, accessKey, secretKey);
      } else {
        throw new Error(createData.message || 'Failed to create video task');
      }
    } catch (error) {
      console.error('Error generating video:', error);
      alert('Failed to generate video. Please try again.');
      setIsGenerating(false);
    }
  };

  const pollVideoStatus = async (taskId: string, accessKey: string, secretKey: string) => {
    const maxAttempts = 60; // 5 minutes max (5 seconds per attempt)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await fetch(`https://api.klingai.com/v1/videos/image2video/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${accessKey}:${secretKey}`
          }
        });

        const data = await response.json();

        if (data.code === 0) {
          const status = data.data?.task_status;
          
          if (status === 'succeed' && data.data?.task_result?.videos?.[0]?.url) {
            setGeneratedVideo(data.data.task_result.videos[0].url);
            setIsGenerating(false);
            return;
          } else if (status === 'failed') {
            throw new Error('Video generation failed');
          } else if (attempts < maxAttempts) {
            // Still processing, check again in 5 seconds
            attempts++;
            setTimeout(checkStatus, 5000);
          } else {
            throw new Error('Video generation timeout');
          }
        } else {
          throw new Error(data.message || 'Failed to check video status');
        }
      } catch (error) {
        console.error('Error checking video status:', error);
        alert('Failed to generate video. Please try again.');
        setIsGenerating(false);
      }
    };

    checkStatus();
  };

  return (
    <div className="section">
      <div className="section-content">
        {/* Upload Image Section */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Upload Jewelry Photo</h3>
          
          <div style={{ 
            border: '2px dashed #d1d5db', 
            borderRadius: '12px', 
            padding: '32px', 
            textAlign: 'center',
            backgroundColor: '#f9fafb'
          }}>
            {!uploadedImage ? (
              <label style={{ cursor: 'pointer' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
                <div>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>📸</div>
                  <div style={{ fontSize: '16px', color: '#6b7280', marginBottom: '8px' }}>
                    Click to upload jewelry photo
                  </div>
                  <div style={{ fontSize: '14px', color: '#9ca3af' }}>
                    PNG, JPG up to 10MB
                  </div>
                </div>
              </label>
            ) : (
              <div>
                <img 
                  src={uploadedImage} 
                  alt="Uploaded jewelry" 
                  style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px' }}
                />
                <button
                  onClick={removeImage}
                  style={{
                    marginTop: '12px',
                    padding: '8px 16px',
                    backgroundColor: '#ef4444',
                    color: '#fff',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  Remove Photo
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Step 1: Platform & Format */}
        <div className="section-content">
          <div className="step-header">
            <div className="step-number">1</div>
            <h3 className="step-title">Platform & Format</h3>
          </div>

          <SelectField
            label="Platform"
            value={formData.platform}
            onChange={(value) => onChange('platform', value)}
            options={[
              'Instagram Post',
              'Instagram Story',
              'Instagram Reel',
              'TikTok',
              'YouTube Shorts',
              'Facebook',
              'Etsy Store',
              'Website'
            ]}
          />

          <SelectField
            label="Aspect Ratio"
            value={formData.aspectRatio}
            onChange={(value) => onChange('aspectRatio', value)}
            options={['16:9', '9:16', '1:1', '4:5']}
          />
        </div>

        {/* Step 2: Describe Your Jewelry */}
        <div className="section-content">
          <div className="step-header">
            <div className="step-number">2</div>
            <h3 className="step-title">Describe Your Jewelry</h3>
          </div>

          <SelectField
            label="Jewelry Item"
            value={formData.jewelryItem}
            onChange={(value) => onChange('jewelryItem', value)}
            options={[
              'Necklace',
              'Earring',
              'Ring',
              'Bracelet',
              'Pendant',
              'Bangle'
            ]}
            allowCustom={true}
          />
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-12px', marginBottom: '16px' }}>
            Select the type of jewelry
          </p>
        </div>

        {/* Video Settings Grid (2 cards per row) */}
        <div className="photo-settings-grid">
          {/* Video 1 Settings */}
          <div className="section-content collapsible-section">
            <div 
              className="step-header collapsible-header" 
              onClick={() => setIsVideo1Expanded(!isVideo1Expanded)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <div className="step-number">3</div>
                <h3 className="step-title">Video 1</h3>
              </div>
              <span style={{ fontSize: '16px', color: '#666' }}>
                {isVideo1Expanded ? '▼' : '▶'}
              </span>
            </div>

            {isVideo1Expanded && (
              <div style={{ marginTop: '16px' }}>
                <SelectField
                  label="Camera Angle"
                  value={formData.cameraAngle}
                  onChange={(value) => onChange('cameraAngle', value)}
                  options={[
                    'Eye Level',
                    'High Angle',
                    'Low Angle',
                    'Bird\'s Eye View',
                    '360 Rotation'
                  ]}
                />

                <SelectField
                  label="Camera Movement"
                  value={formData.cameraMovement}
                  onChange={(value) => onChange('cameraMovement', value)}
                  options={[
                    'Static',
                    'Slow Pan',
                    'Slow Zoom In',
                    'Slow Zoom Out',
                    'Circular Movement',
                    'Tracking Shot'
                  ]}
                />

                <SelectField
                  label="Motion Speed"
                  value={formData.motionSpeed}
                  onChange={(value) => onChange('motionSpeed', value)}
                  options={['Very Slow', 'Slow', 'Medium', 'Fast', 'Very Fast']}
                />

                <SelectField
                  label="Focus Point"
                  value={formData.focusPoint}
                  onChange={(value) => onChange('focusPoint', value)}
                  options={[
                    'On Jewelry',
                    'On Model Wearing Jewelry',
                    'Shifting Focus',
                    'Detail to Full Shot',
                    'Multiple Focus Points'
                  ]}
                />

                <TextInput
                  label="Other Details"
                  value={formData.videoOtherDetails}
                  onChange={(value) => onChange('videoOtherDetails', value)}
                  placeholder="Add custom details, special effects, or specific requirements..."
                  multiline
                />
              </div>
            )}
          </div>

          {/* Video 2 Settings (Placeholder for future) */}
          <div className="section-content collapsible-section">
            <div 
              className="step-header collapsible-header" 
              onClick={() => setIsVideo2Expanded(!isVideo2Expanded)}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <div className="step-number">4</div>
                <h3 className="step-title">Video 2</h3>
              </div>
              <span style={{ fontSize: '16px', color: '#666' }}>
                {isVideo2Expanded ? '▼' : '▶'}
              </span>
            </div>

            {isVideo2Expanded && (
              <div style={{ marginTop: '16px', textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                Additional video settings coming soon...
              </div>
            )}
          </div>
        </div>

        {/* Generate Button */}
        <button 
          className="generate-button" 
          onClick={generateVideoWithKlingAI}
          disabled={isGenerating || !uploadedImage}
          style={{ opacity: (!uploadedImage || isGenerating) ? 0.5 : 1 }}
        >
          {isGenerating ? 'Generating Video...' : 'Generate Video'}
        </button>

        {/* Generated Video Display */}
        {generatedVideo && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Generated Video</h3>
            <video 
              src={generatedVideo} 
              controls 
              style={{ 
                width: '100%', 
                maxWidth: '800px', 
                borderRadius: '12px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            />
            <a 
              href={generatedVideo} 
              download="jewelry-video.mp4"
              style={{
                display: 'inline-block',
                marginTop: '12px',
                padding: '10px 20px',
                backgroundColor: '#3b82f6',
                color: '#fff',
                borderRadius: '6px',
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              Download Video
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoSection;





